import { useEffect, useRef, useState, useMemo } from "react";
import { Box, Button, IconButton, ListItemIcon, MenuItem, Tooltip, useTheme } from "@mui/material";
import { AttachMoney, FileDownload, Refresh } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MRT_ColumnDef } from "material-react-table";
import { toast } from "react-toastify";

import TableView from "../../components/TableView";
import { LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../util/constants";
import { useAuth } from "../../hooks/useAuth";
import { useData } from "../../hooks/useData";
import { useUI } from "../../providers/MoleUIContext";
import { Util } from "../../util/Util";
import { useDateRange, getPeriodQueryParams } from "../../hooks/useDateRange";
import Supplies from "./Supplies";
import SupplierBulkPaymentModal from "../payments/SupplierBulkPaymentModal";
import {
	RECON_CHART_COLORS,
	ReconPhase,
	ReconRow,
	buildWaterfallSteps,
	getChartSurface,
	SituationBalanceFlowChart,
	SituationChartsGrid,
	SituationDetailHeader,
	SituationDetailShell,
	SituationEndBalancesChart,
	SituationEntityComposedChart,
	SituationKpiGrid,
	SituationPeriodAlert,
	SituationReconciliation,
} from "../client/situationDetailShared";
import { createSignCell } from "../../util/cellFormatters";
import { canRecord } from '../../util/access';

const hasSupplierPeriodActivity = (record: any) =>
	["period_supplied", "period_paid", "period_balance"].some(
		(key) => Number(record[key] ?? 0) !== 0,
	);

const SupplierSituation = () => {
	const [toPay, setToPay] = useState<any>(null);
	const [toView, setToView] = useState<any>(null);

	const theme = useTheme();
	const { api, session, access } = useAuth();
	const { setLoadingMessage, t } = useUI();
	const { dateRange: period, setDateRange: setPeriod } = useDateRange(LocalStorageKeys.SUPPLIER_SIT_RECORDS);
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, loading, setLoading } =
		useData(LocalStorageKeys.SUPPLIER_SIT_RECORDS);

	const toastIdRef = useRef<string | number>(-1);
	const firstFetchRef = useRef(false);

	useEffect(() => {
		if (!loadFromIndexedDB() && !firstFetchRef.current) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, []);

	const fetchData = async (useLoadModal: boolean = true) => {
		setLoading(true);

		const res = await api.get("suppliers/situations", {
			params: getPeriodQueryParams(period),
		});

		if (res?.status === 200) {
			saveFromServer(res.data, period);
			toast.update(toastIdRef.current, {
				render: t("common:toast.success.refresh"),
				type: "success",
				isLoading: false,
				autoClose: 3000,
			});
		} else if (res) {
			console.error("Failed to fetch supplier situation:", res);
			toast.dismiss(toastIdRef.current);
		}

		setLoading(false);
	};

	const openPdfBlob = (encoded: string) => {
		const pdfData = atob(encoded);
		const blob = new Blob([Uint8Array.from(pdfData, (c) => c.charCodeAt(0))], {
			type: "application/pdf",
		});
		window.open(URL.createObjectURL(blob));
	};

	const handleChanges = () => {
		toastIdRef.current = toast.loading(
			`${t("common:toast.success.changes")} ${t("common:toast.loading.refresh")}`,
		);
		fetchData(false);
	};

	const exportLedger = async (selectedIds: number[]) => {
		setLoadingMessage("EXPORT");
		const payload: Record<string, unknown> = { ...getPeriodQueryParams(period) };
		if (selectedIds.length > 0) payload.Suppliers = selectedIds;

		const res = await api.post("/reports/supplies/suppliers/ledger", payload);
		if (res?.status === 200) openPdfBlob(res.data.data);
		setLoadingMessage(null);
	};

	const renderSupplierDetailPanel = ({ row }: { row: any }) => {
		const record = row.original;
		const chartSurface = getChartSurface(theme);
		const periodActive = hasSupplierPeriodActivity(record);
		const phaseLabel = (phase: ReconPhase) =>
			t(`common:supplierSituation.detailPanel.phases.${phase}`);

		const supplierRow: ReconRow = {
			label: "Supplier",
			before: {
				invoiced: Number(record.before_supplied ?? 0),
				paid: Number(record.before_paid ?? 0),
				balance: Number(record.before_balance ?? 0),
			},
			period: {
				invoiced: Number(record.period_supplied ?? 0),
				paid: Number(record.period_paid ?? 0),
				balance: Number(record.period_balance ?? 0),
			},
			end: {
				invoiced: Number(record.after_supplied ?? 0),
				paid: Number(record.after_paid ?? 0),
				balance: Number(record.after_balance ?? 0),
			},
		};

		const supplierLabel = t("common:supplierSituation.detailPanel.supplier");
		const waterfallData = buildWaterfallSteps(supplierRow, {
			balance: RECON_CHART_COLORS.balance,
			increase: chartSurface.debitBalance,
			decrease: chartSurface.creditBalance,
		}).map((step, index) => ({
			...step,
			name: (["before", "period", "end"] as ReconPhase[]).map(phaseLabel)[index],
		}));

		return (
			<SituationDetailShell theme={theme}>
				<SituationDetailHeader
					name={record.name}
					subtitle={t("common:supplierSituation.detailPanel.supplier")}
					isActive
					unlimited
					creditLimit={0}
					t={t}
				/>
				<SituationPeriodAlert
					periodActive={periodActive}
					label={t("common:supplierSituation.noPeriodActivity")}
				/>
				<SituationKpiGrid
					theme={theme}
					stats={[
						{
							label: t("common:supplierSituation.endBalance"),
							value: supplierRow.end.balance,
							signed: true,
						},
						{
							label: t("common:supplierSituation.detailPanel.endSupplied"),
							value: supplierRow.end.invoiced,
							signed: false,
						},
						{
							label: t("common:supplierSituation.detailPanel.endPaid"),
							value: supplierRow.end.paid,
							signed: false,
						},
						{
							label: t("common:supplierSituation.detailPanel.periodChange"),
							value: supplierRow.period.balance,
							signed: true,
						},
					]}
				/>
				<SituationReconciliation
					rows={[supplierRow]}
					periodActive={periodActive}
					getRowLabel={() => supplierLabel}
					t={t}
					theme={theme}
					i18nNamespace="common:supplierSituation.detailPanel"
				/>
				<SituationChartsGrid
					chartSurface={chartSurface}
					periodActive={periodActive}
					overviewTitle={t("common:supplierSituation.detailPanel.charts.endOverview")}
					overviewChart={(
						<SituationEntityComposedChart
							data={[{
								entity: supplierLabel,
								invoiced: supplierRow.end.invoiced,
								paid: supplierRow.end.paid,
								balance: supplierRow.end.balance,
							}]}
							t={t}
							theme={theme}
							i18nNamespace="common:supplierSituation.detailPanel"
						/>
					)}
					periodTitle={t("common:supplierSituation.detailPanel.charts.periodActivity")}
					periodChart={(
						<SituationEntityComposedChart
							data={[{
								entity: supplierLabel,
								invoiced: supplierRow.period.invoiced,
								paid: supplierRow.period.paid,
								balance: supplierRow.period.balance,
							}]}
							t={t}
							theme={theme}
							i18nNamespace="common:supplierSituation.detailPanel"
						/>
					)}
					endBalanceTitle={t("common:supplierSituation.detailPanel.charts.endBalance")}
					endBalanceChart={(
						<SituationEndBalancesChart
							data={[{
								entity: supplierLabel,
								balance: supplierRow.end.balance,
								fill: supplierRow.end.balance > 0
									? chartSurface.debitBalance
									: RECON_CHART_COLORS.balance,
							}]}
							t={t}
							theme={theme}
						/>
					)}
					balanceFlowTitle={t("common:supplierSituation.detailPanel.charts.balanceFlow")}
					balanceFlowChart={(
						<SituationBalanceFlowChart data={waterfallData} t={t} theme={theme} />
					)}
				/>
			</SituationDetailShell>
		);
	};

	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.supplier"),
			accessorKey: "name",
			Cell: ({ cell }: { cell: any }) => (
				<button
					type="button"
					style={{
						background: "none",
						border: "none",
						padding: 0,
						color: "inherit",
						cursor: "pointer",
						textDecoration: "underline",
					}}
					onClick={() => setToView(cell.row.original)}
					disabled={Number(cell.row.original.after_balance ?? 0) === 0 || loading}
				>
					{cell.getValue()}
				</button>
			),
			filterVariant: "autocomplete",
			filterFn: "contains",
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.owing"),
			accessorFn: (row: any) => Number(row.after_balance),
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: "contains",
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
	], [loading, t]);

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
					stateKey={LocalStorageKeys.SUPPLIER_SIT_RECORDS}
					header={t("common:supplierSituation.headers.suppliers")}
					data={data}
					columns={columns}
					includeTimestampColumns={false}
					totals={[{ key: "after_balance", label: t("common:tables.owing") }]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={setPeriod}
					isLoading={loading}
					renderDetailPanel={renderSupplierDetailPanel}
					renderTopToolbarActions={(table) => (
						<Box sx={{ display: "flex", gap: "0.5rem" }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							<Button
								color="success"
								startIcon={<FileDownload />}
								variant="outlined"
								onClick={() => {
									const selectedIds = table
										.getSelectedRowModel()
										.rows.map((row) => row.original.id);
									exportLedger(selectedIds);
								}}
							>
								{t("common:supplierSituation.exportLedger")}
							</Button>
						</Box>
					)}
					renderRowActionMenuItems={(row, closeMenu) => [
						Number(row.original.after_balance ?? 0) !== 0 && (
							<MenuItem
								key="pay"
								disabled={toPay}
								onClick={() => {
									if (
										canRecord(access, 'supply_payment')
										|| canRecord(access, 'payment')
										|| canRecord(access, 'supply')
									) {
										setToPay({ ...row.original, supplier: row.original.name });
									}
									closeMenu();
								}}
								sx={{ color: "success.main" }}
							>
								<ListItemIcon sx={{ color: "success.main" }}>
									<AttachMoney />
								</ListItemIcon>
								{t("common:ui.buttons.pay")}
							</MenuItem>
						),
					]}
				/>
			</LocalizationProvider>

			{toView && (
				<Supplies
					title={t("common:supplierSituation.unpaidSupplySituation", { name: toView.name })}
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={toView}
					onPayment={handleChanges}
				/>
			)}

			{toPay && (
				<SupplierBulkPaymentModal
					isOpen={!!toPay}
					toggle={() => setToPay(null)}
					bundle={toPay}
					onSuccess={handleChanges}
				/>
			)}
		</>
	);
};

export default SupplierSituation;